import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  Button,
  ButtonProps,
} from '@elementary/common-ui/components';
import {
  SectionTitle,
  SectionDescription,
  InfoBox,
} from '@elementary/common-ui/components/FormControls';
import { Link } from '@mui/material';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types/types';
import { DbtProjectFormData } from 'shared/types';
import DbtProjectCard from './DbtProjectCard';

type Props = {
  warehouseType: WarehouseType;
  existingProjectIds?: string[];
  disabled?: boolean;
};

const Wrapper = styled(FlexColumn)`
  gap: 24px;
`;

const ProjectsContainer = styled.div`
  border-radius: 6px;
  overflow: hidden;
`;

const AddProjectButton = styled(Button)<ButtonProps>`
  && {
    width: 100%;
    border: 1px solid ${({ theme }) => theme.palette.common.gray5};
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.palette.common.white};
    color: ${({ theme }) => theme.palette.common.black};
    height: 52px;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.palette.common.gray1};
    }

    &:disabled {
      color: ${({ theme }) => theme.palette.common.gray11};
    }
  }
`;

const getDefaultProject = (
  warehouseType: WarehouseType,
): DbtProjectFormData => {
  const hasDatabase = warehouseType !== 'clickhouse';
  return {
    name: '',
    schema: '',
    database: hasDatabase ? '' : undefined,
  };
};

const EnvironmentContentStep = ({
  warehouseType,
  existingProjectIds = [],
  disabled,
}: Props) => {
  const { control, watch } = useFormContext();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dbtProjects',
  });

  const dbtProjects = watch('dbtProjects') as DbtProjectFormData[];

  const handleAddProject = () => {
    append(getDefaultProject(warehouseType));
    setExpandedIndex(fields.length);
  };

  const handleToggleAccordion = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleRemoveProject = (index: number) => {
    remove(index);
  };

  const isExistingProject = (index: number) => {
    const project = dbtProjects[index];
    return project?.id != null && existingProjectIds.includes(project.id);
  };

  return (
    <Wrapper>
      <FlexColumn gap="12px">
        <SectionTitle>DBT PROJECTS</SectionTitle>
        <SectionDescription>
          Connect the dbt projects you wish to include in the lineage for this
          environment.
        </SectionDescription>
      </FlexColumn>

      <InfoBox>
        Install{' '}
        <Link
          href="https://docs.elementary-data.com/cloud/onboarding/quickstart-dbt-package"
          target="_blank"
        >
          elementary dbt package
        </Link>{' '}
        in the target dbt project.
      </InfoBox>
      <ProjectsContainer>
        {fields.map((field, index) => (
          <DbtProjectCard
            key={field.id}
            index={index}
            projectName={dbtProjects[index]?.name}
            warehouseType={warehouseType}
            isExisting={isExistingProject(index)}
            onRemove={() => handleRemoveProject(index)}
            canRemove={fields.length > 1}
            disabled={disabled}
            isFirst={index === 0}
            expanded={expandedIndex === index}
            onToggle={() => handleToggleAccordion(index)}
          />
        ))}
        <AddProjectButton
          variant="text"
          startIcon={<PlusIcon />}
          onClick={handleAddProject}
          disabled={disabled}
        >
          Add dbt project
        </AddProjectButton>
      </ProjectsContainer>
    </Wrapper>
  );
};

export default EnvironmentContentStep;
